/**
 * telephonyTransport — the selection seam for the telephony transport.
 *
 * Decouples the call-session mechanism (Telnyx WebRTC today, a native Capacitor
 * plugin tomorrow) from Redux/UI/post-call-save. Every call origin goes through
 * getTelephonyTransport() instead of touching telnyxService directly.
 *
 * Selection is by getIsNativePlatform():
 *   - web (browser / web build): the behavior-preserving Telnyx WebRTC wrapper.
 *   - native: the Capacitor `NativeCalling` plugin bridge (stubbed contract).
 *
 * The native transport is built lazily so its registerPlugin/addListener
 * side-effects never run in the browser bundle — on web it is never resolved.
 *
 * Both implementations satisfy the same interface:
 *   methods: login, disconnect, dial, answer, hangup, mute, hold, sendDtmf,
 *            registerPushToken, deregisterPushToken
 *   events (via on/off): incomingCall, callAnswered, callEnded, callStateChanged
 */
import { getIsNativePlatform } from 'utils/platform';

import createNativeTelephonyTransport from './nativeTelephonyTransport';
import webTelephonyTransport from './webTelephonyTransport';

const cache = {
  instance: null,
};

const resolveTransport = () => {
  if (getIsNativePlatform()) {
    return createNativeTelephonyTransport();
  }

  return webTelephonyTransport;
};

/**
 * Returns the active telephony transport singleton. Resolved once on first
 * access for the lifetime of the app session (platform does not change at
 * runtime), so listeners registered on it remain stable.
 */
export const getTelephonyTransport = () => {
  if (!cache.instance) {
    cache.instance = resolveTransport();
  }

  return cache.instance;
};
